import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { isNil } from "lodash";
import {
  Fade
} from "@material-ui/core";
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputBase,
  makeStyles,
  Paper,
  Hidden,
  Menu,
  MenuItem,
  Tooltip,
  Fab,
  Chip,
  Box,
  Typography,
  Badge,
} from "@material-ui/core";
import { blue, green, pink, grey } from "@material-ui/core/colors";
import {
  CheckCircleOutline,
  Clear,
  Comment,
  Create,
  Description,
  HighlightOff,
  Mic,
  Mood,
  MoreVert,
  Send,
  PermMedia,
  Person,
  Reply,
  Duo,
  Timer,
  WhatsApp,
  Info,
  AccountTree
} from "@material-ui/icons";

import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatStrikethrough as FormatStrikethroughIcon,
  Code as CodeIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatQuote as FormatQuoteIcon,
  FormatClear as FormatClearIcon,
} from "@material-ui/icons";

import AddIcon from "@material-ui/icons/Add";
import { CameraAlt } from "@material-ui/icons";
import MicRecorder from "mic-recorder-to-mp3";
import clsx from "clsx";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import getAudioDurationSeconds from "../../utils/getAudioDurationSeconds";

import useQuickMessages from "../../hooks/useQuickMessages";
import { isString, isEmpty } from "lodash";
import ContactSendModal from "../ContactSendModal";
import CameraModal from "../CameraModal";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import MessageUploadMedias from "../MessageUploadMedias";
import { EditMessageContext } from "../../context/EditingMessage/EditingMessageContext";
import ScheduleModal from "../ScheduleModal";
import { fetchScheduleDraft } from "../../utils/scheduleDraft";
import usePlans from "../../hooks/usePlans";
import TemplateModal from "../TemplateMetaModal";
import TriggerFlowModal from "../TriggerFlowModal";
import EditMessageModal from "../EditMessageModal";
import PixSendModal from "../PixSendModal";
import LocationSendModal from "../LocationSendModal";
import PollSendModal from "../PollSendModal";
import StickerPicker from "../StickerPicker";
import PixIcon from "@mui/icons-material/Pix";
import { LocationOn as LocationOnIcon, Poll as PollIcon } from "@material-ui/icons";


// ============== [DRAFT] Constantes e helpers de persistência ==============
const DRAFT_KEY_PREFIX = "messageDraft_";
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 dias
const DRAFT_DEBOUNCE_MS = 400;

/**
 * Lê o rascunho salvo no localStorage para um ticket.
 * Compatível com formato legado (string simples) e formato novo ({ text, savedAt }).
 * @param {string|number} ticketId
 * @returns {{ text: string, savedAt: number } | null}
 */
const readDraftFromStorage = (ticketId) => {
  if (!ticketId) return null;
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${ticketId}`);
    if (!raw) return null;
    // Formato novo: JSON { text, savedAt }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.text === "string") {
        return parsed;
      }
    } catch (_) {
      // Formato legado: string pura
    }
    // Fallback legado: tratar raw como texto direto
    return { text: raw, savedAt: Date.now() };
  } catch (err) {
    // Quota excedida, modo privado, etc.
    return null;
  }
};

/**
 * Persiste o rascunho no localStorage. Se o texto for vazio, remove a chave.
 * @param {string|number} ticketId
 * @param {string} text
 */
const writeDraftToStorage = (ticketId, text) => {
  if (!ticketId) return;
  try {
    const key = `${DRAFT_KEY_PREFIX}${ticketId}`;
    if (!text || text.trim() === "") {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify({ text, savedAt: Date.now() }));
  } catch (err) {
    // Quota excedida, modo privado, etc. — silencioso, nunca bloqueia UI
  }
};

/**
 * Remove rascunhos com mais de DRAFT_TTL_MS. Converte legados para o formato novo ou remove.
 * Chamado 1× no mount do componente.
 */
const purgeStaleDrafts = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.savedAt === "number") {
          if (now - parsed.savedAt > DRAFT_TTL_MS) {
            keysToRemove.push(key);
          }
          continue;
        }
      } catch (_) {
        // String legada sem savedAt → remover (impossível saber idade)
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    // Silencioso
  }
};

/**
 * Remove o draft de um ticket específico e cancela qualquer debounce pendente.
 * @param {string|number} ticketId
 * @param {React.MutableRefObject} timerRef
 */
const clearDraftForTicket = (ticketId, timerRef) => {
  if (timerRef && timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
  if (!ticketId) return;
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${ticketId}`);
  } catch (_) {
    // Silencioso
  }
};
// ============== [DRAFT] Fim dos helpers ==============

const Mp3Recorder = new MicRecorder({
  bitRate: 128,
  sampleRate: 44100
});

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },
  "@keyframes pulse": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.7)",
    },
    "70%": {
      boxShadow: "0 0 0 10px rgba(25, 118, 210, 0)",
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)",
    },
  },
  avatar: {
    width: "50px",
    height: "50px",
    borderRadius: "25%",
  },
  dropInfo: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: 15,
    left: 0,
    right: 0,
  },
  dropInfoOut: {
    display: "none",
  },
  gridFiles: {
    maxHeight: "100%",
    overflow: "scroll",
  },
  newMessageBox: {
    background: theme.palette.background.default,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },
  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.background.paper,
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative", // ✅ Essencial para o position absolute funcionar
    zIndex: 10, // ✅ Z-index base
  },

  messageInputWrapperPrivate: {
    padding: 6,
    marginRight: 7,
    background: "#F0E68C",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative", // ✅ Essencial para o position absolute funcionar
    zIndex: 10, // ✅ Z-index base
  },

  messageInputWrapperPending: {
    padding: 6,
    marginRight: 7,
    background: "#FFE0B2",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative", // ✅ Essencial para o position absolute funcionar
    border: "2px solid #FF9800",
    zIndex: 10, // ✅ Z-index base
  },
  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },
  messageInputPrivate: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: grey[800],
  },
  messageInputPending: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: "#E65100",
    fontWeight: 500,
  },
  sendMessageIcons: {
    color: grey[700],
  },
  ForwardMessageIcons: {
    color: grey[700],
    transform: "scaleX(-1)",
  },
  uploadInput: {
    display: "none",
  },
  viewMediaInputWrapper: {
    maxHeight: "100%",
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },
  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },
  audioLoading: {
    color: green[500],
    opacity: "70%",
  },
  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },
  cancelAudioIcon: {
    color: "red",
  },
  sendAudioIcon: {
    color: "green",
  },
  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
    backgroundColor: theme.palette.optionsBackground,
  },
  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: theme.mode === "light" ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },
  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },
  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },
  floatingFormatMenu: {
    position: 'fixed',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px',
    border: `1px solid ${theme.palette.divider}`,
  },

  formatIconButton: {
    padding: '6px',
    borderRadius: '4px',
    minWidth: '32px',
    height: '32px',
  },
  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "100%", // ✅ Posicionar acima do input
    background: theme.palette.background.default,
    padding: 0,
    border: "none",
    left: 0,
    right: 0, // ✅ Usar right: 0 em vez de width: 100%
    maxHeight: "200px",
    overflowY: "auto",
    overflowX: "hidden",
    boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.15)",
    borderRadius: "8px 8px 0 0",
    zIndex: 1000, // ✅ Z-index menor para evitar conflitos
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "auto",
        "&:hover": {
          background: theme.palette.background.paper,
          cursor: "pointer",
        },
      },
    },
  },
  quickAnswerItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    minHeight: "48px",
    cursor: "pointer", // ✅ Adicionar cursor pointer
    borderRadius: "4px", // ✅ Bordas arredondadas
    margin: "2px 4px", // ✅ Pequena margem
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    transition: "all 0.2s ease-in-out",
  },

  // ✅ NOVO: Estilo para item selecionado via teclado
  quickAnswerItemSelected: {
    backgroundColor: theme.palette.primary.light + "30", // ✅ Cor semi-transparente
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    fontWeight: 500,
  },

  // ✅ NOVO: Indicador de scroll
  quickAnswersScrollIndicator: {
    textAlign: "center",
    padding: theme.spacing(1),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  quickAnswerText: {
    flex: 1,
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  mediaTypeChip: {
    height: 20,
    fontSize: "0.7rem",
  },
  invertedFabMenu: {
    border: "none",
    borderRadius: 50,
    boxShadow: "none",
    padding: theme.spacing(1),
    backgroundColor: "transparent",
    color: "grey",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&:disabled": {
      backgroundColor: "transparent !important",
    },
  },
  invertedFabMenuMP: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "transparent",
    color: blue[800],
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  invertedFabMenuCont: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    minHeight: "auto",
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "transparent",
    color: blue[500],
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  invertedFabMenuMeet: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    minHeight: "auto",
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "transparent",
    color: green[500],
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  invertedFabMenuDoc: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "transparent",
    color: "#7f66ff",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  invertedFabMenuCamera: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: "transparent",
    color: pink[500],
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  flexContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  flexItem: {
    flex: 1,
  },
  pendingAlert: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: "#E3F2FD",
    border: "1px solid #2196F3",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#1976D2",
    fontSize: "0.875rem",
  },
}));

/** Tipo de mídia para preview otimista (alinhado ao backend). */
const inferMediaTypeFromFile = (file) => {
  const t = file?.type || "";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/")) return "audio";
  if (t === "application/pdf") return "application/pdf";
  if (t.startsWith("application/")) return "document";
  return "document";
};

const MessageInput = ({
  ticketId,
  ticketStatus,
  droppedFiles,
  contactId,
  ticketChannel,
  whatsappId,
  ticketUserId,
  ticketQueueId,
  onFilesProcessed,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [mediasUpload, setMediasUpload] = useState([]);
  const isMounted = useRef(true);

  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  /** Timer de debounce do indicador de digitação (composing → paused). */
  const typingTimerRef = useRef(null);
  /** Evita mandar "composing" repetido se já está digitando. */
  const isTypingRef = useRef(false);
  /** Evita bloquear resposta rápida com mídia quando `loading` global fica true (outro envio, bug, etc.) */
  const quickMessageMediaBusyRef = useRef(false);
  /** ID da última mensagem otimista (ref evita no-undef do ESLint no `catch` com `let` no mesmo bloco). */
  const optimisticClientTempIdRef = useRef(null);
  /** Enter envia no keyDown (após preventDefault o keypress não dispara no Chrome). */
  const handleSendMessageRef = useRef(() => {});
  // ============== [DRAFT] Ref do debounce de persistência ==============
  const draftTimerRef = useRef(null);
  const [onDragEnter, setOnDragEnter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { setEditingMessage, editingMessage } = useContext(EditMessageContext);
  const { user, socket } = useContext(AuthContext);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [pollModalOpen, setPollModalOpen] = useState(false);
  const [stickerPickerOpen, setStickerPickerOpen] = useState(false);
  const [hasScheduleDraft, setHasScheduleDraft] = useState(false);
  const { getPlanCompany } = usePlans();

  const [signMessagePar, setSignMessagePar] = useState(false);
  const { get: getSetting } = useCompanySettings();
  const [signMessage, setSignMessage] = useState(true);
  const [privateMessage, setPrivateMessage] = useState(false);
  const [privateMessageInputVisible, setPrivateMessageInputVisible] =
    useState(false);
  const [senVcardModalOpen, setSenVcardModalOpen] = useState(false);
  const [showModalMedias, setShowModalMedias] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [useWhatsappOfficial, setUseWhatsappOfficial] = useState(false);
  const { list: listQuickMessages } = useQuickMessages();

  const isMobile = useMediaQuery("(max-width: 767px)");
  /** Mesmo critério do menu `Hidden only={md,lg,xl}`: xs/sm usam câmera nativa em vez do modal (getUserMedia quebra muito no mobile). */
  const useNativeCameraCapture = useMediaQuery(theme.breakpoints.down("md"));
  const cameraCaptureInputRef = useRef(null);
  const [placeholderText, setPlaceHolderText] = useState("");

  const [selectedQuickAnswerIndex, setSelectedQuickAnswerIndex] = useState(-1);
  const [isNavigatingQuickAnswers, setIsNavigatingQuickAnswers] = useState(false);

  const [triggerFlowModalOpen, setTriggerFlowModalOpen] = useState(false);
  const [flowProcessing, setFlowProcessing] = useState(false);
  const flowProcessingRef = useRef(false);

  const [formatMenuAnchorPosition, setFormatMenuAnchorPosition] = useState(null);
  const [selectedText, setSelectedText] = useState({ text: '', start: 0, end: 0 });
  /** Payload de botões/lista da resposta rápida selecionada (enviado no próximo envio) */
  const [pendingInteractivePayload, setPendingInteractivePayload] = useState(null);

  // ✅ Mention autocomplete state
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionList, setMentionList] = useState([]);
  const [mentionListFiltered, setMentionListFiltered] = useState([]);
  const [showMentionList, setShowMentionList] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [selectedMentions, setSelectedMentions] = useState([]); // [{jid, name}]
  const mentionCacheRef = useRef({}); // Cache by contactId
  const handleMentionSelectRef = useRef(() => {});

  // Estado para janela de 24h do WhatsApp Oficial
  const [needsTemplate, setNeedsTemplate] = useState(false);
  const [isWhatsAppOfficial, setIsWhatsAppOfficial] = useState(false);
  const [, setWindowCheckLoading] = useState(false);

  const isTicketPending = () => {
    return ticketStatus === "pending";
  };

  useEffect(() => {
    if (ticketStatus === "pending") {
      setPrivateMessage(true);
      setPrivateMessageInputVisible(true);
    } else {
      // Quando o ticket não está mais pending (ex: mudou para "open"), desabilitar mensagem interna
      setPrivateMessage(false);
      setPrivateMessageInputVisible(false);
    }
  }, [ticketStatus]);

  useEffect(() => {
    async function fetchTemplates() {
      if (!whatsappId) {
        setTemplates([]);
        return;
      }
      const { data } = await api.request({
        url: `/quick-messages/list`,
        method: "GET",
        params: {
          isOficial: "true",
          userId: user.id,
          companyId: user.companyId,
          status: "APPROVED",
          whatsappId,
        },
      });
      setTemplates(data || []);
    }
    if (useWhatsappOfficial) {
      fetchTemplates();
    } else {
      setTemplates([]);
    }
  }, [useWhatsappOfficial, whatsappId, user.id, user.companyId]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      setShowSchedules(planConfigs.plan.useSchedules);
      setUseWhatsappOfficial(planConfigs.plan.useWhatsappOfficial);
    }
    fetchData();
    // getPlanCompany vem de usePlans sem useCallback — incluir na deps dispara loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.companyId]);

  useEffect(() => {
    if (!showSchedules || !user?.id) {
      setHasScheduleDraft(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { hasDraft } = await fetchScheduleDraft({ ticketId, contactId });
        if (!cancelled) setHasScheduleDraft(!!hasDraft);
      } catch {
        if (!cancelled) setHasScheduleDraft(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showSchedules, ticketId, contactId, user?.id, appointmentModalOpen]);

  // Função para verificar janela de 24h (reutilizável)
  const checkWhatsAppWindow = useCallback(async () => {
    if (!ticketId || ticketChannel !== "whatsapp_oficial") {
      setNeedsTemplate(false);
      setIsWhatsAppOfficial(false);
      return;
    }

    try {
      setWindowCheckLoading(true);
      const { data } = await api.get(`/tickets/${ticketId}/check-whatsapp-window`);
      
      setIsWhatsAppOfficial(data.isWhatsAppOfficial);
      setNeedsTemplate(data.needsTemplate);
      
      console.log("📱 WhatsApp Window Check:", {
        needsTemplate: data.needsTemplate,
        isWithin24Hours: data.isWithin24Hours,
        hoursElapsed: data.hoursElapsed
      });
    } catch (err) {
      console.error("Erro ao verificar janela de 24h:", err);
      setNeedsTemplate(false);
    } finally {
      setWindowCheckLoading(false);
    }
  }, [ticketId, ticketChannel]);

  // Verificar janela de 24h do WhatsApp Oficial
  useEffect(() => {
    checkWhatsAppWindow();
  }, [checkWhatsAppWindow]);

  // ✅ Re-verificar janela de 24h quando uma nova mensagem do contato chegar
  useEffect(() => {
    if (!ticketId || ticketChannel !== "whatsapp_oficial" || !socket || !user?.companyId) {
      // ✅ Silenciar aviso quando não é WhatsApp Oficial (comportamento esperado)
      // Apenas logar em modo debug se necessário
      // console.log("⚠️ Socket listener não iniciado:", { 
      //   hasTicketId: !!ticketId, 
      //   isWhatsAppOficial: ticketChannel === "whatsapp_oficial",
      //   hasSocket: !!socket,
      //   hasCompanyId: !!user?.companyId
      // });
      return;
    }

    const companyId = user.companyId;
    
    console.log("🎧 Iniciando listener de socket para janela de 24h", {
      ticketId,
      companyId,
      channel: ticketChannel
    });

    const onAppMessage = (data) => {
      console.log("📩 Evento socket recebido:", {
        action: data.action,
        ticketUuid: data.message?.ticket?.uuid,
        ticketId: data.message?.ticket?.id,
        currentTicketId: ticketId,
        fromMe: data.message?.fromMe,
        messageId: data.message?.id
      });

      // ✅ Quando uma nova mensagem é criada E não é do usuário (é do contato)
      // Verificar tanto por UUID quanto por ID numérico para garantir compatibilidade
      const isThisTicket = data.message?.ticket?.uuid === ticketId || 
                           data.message?.ticket?.id === ticketId ||
                           data.ticket?.uuid === ticketId ||
                           data.ticket?.id === ticketId;

      if (data.action === "create" && isThisTicket && !data.message?.fromMe) {
        console.log("✅ Nova mensagem do contato detectada - Re-verificando janela de 24h");
        // Aguardar um pouco para garantir que o backend atualizou lastInteractionClient
        setTimeout(() => {
          checkWhatsAppWindow();
        }, 1000);
      }
    };

    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      console.log("🔌 Removendo listener de socket");
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [ticketId, ticketChannel, socket, user?.companyId, checkWhatsAppWindow]);

  useEffect(() => {
    let nextBase = "";
    if (needsTemplate && isWhatsAppOfficial) {
      nextBase = "⚠️ Janela de 24h expirada - Envie um template primeiro";
    } else if (ticketStatus === "open" || ticketStatus === "group") {
      nextBase = i18n.t("messagesInput.placeholderOpen");
    } else if (ticketStatus === "pending") {
      nextBase = "Digite sua mensagem interna (ticket aguardando)...";
    } else {
      nextBase = i18n.t("messagesInput.placeholderClosed");
    }

    const maxLength = isMobile ? 20 : Infinity;
    if (isMobile && nextBase.length > maxLength) {
      setPlaceHolderText(nextBase.substring(0, maxLength) + "...");
    } else {
      setPlaceHolderText(nextBase);
    }
  }, [ticketStatus, needsTemplate, isWhatsAppOfficial, isMobile]);

  const {
    selectedMessages,
    setForwardMessageModalOpen,
    showSelectMessageCheckbox,
  } = useContext(ForwardMessageContext);

  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      // Processar imediatamente sem conversões desnecessárias
      // droppedFiles já é um array quando vem do drop
      const selectedMedias = Array.isArray(droppedFiles) 
        ? droppedFiles 
        : Array.from(droppedFiles);
      
      // Atualizar estado e abrir modal imediatamente
      setMediasUpload(selectedMedias);
      setShowModalMedias(true);
      
      // Notificar o componente pai que os arquivos foram processados
      // Chamar de forma assíncrona para não bloquear a renderização do modal
      if (onFilesProcessed) {
        requestAnimationFrame(() => {
          onFilesProcessed();
        });
      }
    }
  }, [droppedFiles, onFilesProcessed]);

  useEffect(() => {
    // ============== [DRAFT] Purga de rascunhos antigos (1× no mount) ==============
    purgeStaleDrafts();
    return () => {
      isMounted.current = false;
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      // ============== [DRAFT] Limpar debounce pendente no unmount ==============
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, []);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMediasUpload([]);
      setReplyingMessage(null);
      if (ticketStatus !== "pending") {
        setPrivateMessage(false);
        setPrivateMessageInputVisible(false);
      }
      setEditingMessage(null);
    };
  }, [ticketId, ticketStatus, setReplyingMessage, setEditingMessage]);

  // ============== [DRAFT] Restaurar rascunho ao trocar de ticket ==============
  // Executa APÓS o cleanup do useEffect acima (que faz setInputMessage("")),
  // portanto o draft restaurado prevalece sobre o reset.
  useEffect(() => {
    if (!ticketId) return;
    const draft = readDraftFromStorage(ticketId);
    if (draft && draft.text) {
      setInputMessage(draft.text);
      // Posicionar cursor no final após restauração
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = draft.text.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }, 50);
    }
  }, [ticketId]);

  // ============== [DRAFT] Persistência centralizada (cobre TODOS os 15 pontos de mutação) ==============
  // Observa [inputMessage, ticketId] — qualquer alteração (digitação, emoji, menção,
  // formatação, quick reply, Jitsi link) é capturada automaticamente.
  useEffect(() => {
    // Capturar valores atuais na closure para segurança na troca de ticket
    const currentTicketId = ticketId;
    const currentText = inputMessage;

    if (!currentTicketId) return;

    // Texto vazio → remover draft imediatamente (sem debounce)
    if (!currentText || currentText.trim() === "") {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      writeDraftToStorage(currentTicketId, "");
      return;
    }

    // Texto preenchido → debounce de 400ms
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      writeDraftToStorage(currentTicketId, currentText);
      draftTimerRef.current = null;
    }, DRAFT_DEBOUNCE_MS);

    // Cleanup: flush imediato na troca de ticket (garante zero perda de caracteres)
    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      // Salvar snapshot atual antes de sair — closure segura com currentTicketId
      if (currentText && currentText.trim() !== "") {
        writeDraftToStorage(currentTicketId, currentText);
      }
    };
  }, [inputMessage, ticketId]);
  // ============== [DRAFT] Fim da persistência centralizada ==============

  useEffect(() => {
    let isProcessing = false; // ✅ Flag para evitar processamento duplo

    const handleInsertQuickMessage = (event) => {
      // ✅ IMPORTANTE: Evitar processamento duplo
      if (isProcessing) {
        console.log("⚠️ Já processando evento, ignorando...");
        return;
      }

      isProcessing = true;
      console.log("📥 Evento insertQuickMessage recebido:", event.detail);

      const { quickMessage } = event.detail;

      if (!quickMessage) {
        console.error("❌ quickMessage não encontrado no evento");
        isProcessing = false;
        return;
      }

      console.log("🔍 Processando quickMessage:", {
        hasMedia: !!quickMessage.mediaPath,
        mediaType: quickMessage.mediaType,
        message: quickMessage.message,
        ticketId: ticketId
      });

      if (quickMessage.mediaPath) {
        console.log("🎵 Processando resposta rápida com mídia");
        console.log("📝 mediaName disponível no quickMessage:", quickMessage.mediaName);
        handleQuickAnswersClick({
          value: quickMessage.message || "",
          mediaPath: quickMessage.mediaPath,
          mediaName: quickMessage.mediaName || null, // ✅ CORREÇÃO: Incluir mediaName
          mediaType: quickMessage.mediaType,
          shortcode: quickMessage.shortcode,
          label: `/${quickMessage.shortcode} - ${quickMessage.message}`
        }).finally(() => {
          isProcessing = false; // ✅ Liberar flag após processamento
        });
      } else {
        console.log("📝 Processando resposta rápida de texto");
        const currentText = inputMessage?.trim() || "";
        const newText = currentText
          ? `${currentText}\n\n${quickMessage.message}`
          : quickMessage.message;

        setInputMessage(newText);
        if (quickMessage.interactivePayload) {
          try {
            setPendingInteractivePayload(JSON.parse(quickMessage.interactivePayload));
          } catch (_) {
            setPendingInteractivePayload(null);
          }
        } else {
          setPendingInteractivePayload(null);
        }

        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            const length = newText.length;
            inputRef.current.setSelectionRange(length, length);
          }
          isProcessing = false; // ✅ Liberar flag
        }, 100);
      }
    };

    // ✅ IMPORTANTE: Escutar apenas no window
    window.addEventListener('insertQuickMessage', handleInsertQuickMessage);

    return () => {
      window.removeEventListener('insertQuickMessage', handleInsertQuickMessage);
      isProcessing = false; // ✅ Reset da flag no cleanup
    };
    // handleQuickAnswersClick é estável o suficiente via closure na mesma render; incluir aqui exigiria mover o hook abaixo da definição.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evita TDZ e re-subscrição excessiva
  }, [inputMessage, ticketId, privateMessage]);

  const dragEnterActive = onDragEnter === true;
  useEffect(() => {
    setTimeout(() => {
      if (isMounted.current) setOnDragEnter(false);
    }, 1000);
  }, [dragEnterActive]);

  // ✅ Fetch group participants for mention autocomplete
  useEffect(() => {
    const fetchParticipants = async () => {
      if (ticketStatus !== "group" || !contactId) return;

      // Check cache first
      if (mentionCacheRef.current[contactId]) {
        setMentionList(mentionCacheRef.current[contactId]);
        return;
      }

      try {
        const { data } = await api.get(`/contacts/${contactId}/participants`);
        if (data && Array.isArray(data)) {
          const participants = data.map(p => ({
            jid: p.id, // Full JID (can be @s.whatsapp.net or @lid)
            name: p.name || p.number || p.id,
            number: p.number || "",
          }));
          mentionCacheRef.current[contactId] = participants;
          setMentionList(participants);
        }
      } catch (err) {
        console.log("[MentionAutocomplete] Error fetching participants:", err.message);
      }
    };

    fetchParticipants();
  }, [ticketStatus, contactId]);

  useEffect(() => {
    const fetchSettings = async () => {
      const setting = await getSetting({
        column: "sendSignMessage",
      });

      if (isMounted.current) {
        // Permissao individual do usuario tem PRIORIDADE sobre config da empresa
        if (user?.allowHideSignature === "enabled") {
          // "Habilitado" → oculta assinatura SEMPRE, sem toggle (lapis nao aparece)
          setSignMessagePar(false);
          setSignMessage(false);
        } else if (user?.allowHideSignature === "optional") {
          // "Opcional" → mostra o lapis para o usuario escolher por mensagem
          setSignMessagePar(true);
          const signMessageStorage = JSON.parse(
            localStorage.getItem("persistentSignMessage")
          );
          if (isNil(signMessageStorage)) {
            setSignMessage(true);
          } else {
            setSignMessage(signMessageStorage);
          }
        } else if (setting.sendSignMessage === "enabled") {
          // "Desabilitado" no user + empresa habilitada → lapis para todos (comportamento original)
          setSignMessagePar(true);
          const signMessageStorage = JSON.parse(
            localStorage.getItem("persistentSignMessage")
          );
          if (isNil(signMessageStorage)) {
            setSignMessage(true);
          } else {
            setSignMessage(signMessageStorage);
          }
        } else {
          // "Desabilitado" no user + empresa desabilitada → assinatura sempre ativa, sem toggle
          setSignMessagePar(false);
          setSignMessage(true);
        }
      }
    };
    fetchSettings();
  }, [getSetting, user]);

  // CORREÇÃO DO ERRO charAt - Função mais robusta
  const safeCapitalizeFirstLetter = (string) => {
    if (!string || typeof string !== 'string') return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // FUNÇÕES PARA TRIGGER FLOW MODAL
  const handleTriggerFlowClick = useCallback(() => {
    console.log("🎯 Abrindo modal de fluxo");

    // 🛡️ RESET PREVENTIVO
    setFlowProcessing(false);
    flowProcessingRef.current = false;

    setTriggerFlowModalOpen(true);
  }, []);

  const handleTriggerFlowClose = useCallback(() => {
    console.log("🚪 Fechando modal");

    setFlowProcessing(false);
    flowProcessingRef.current = false;
    setTriggerFlowModalOpen(false);
  }, []);

  const handleFlowProcessing = useCallback((isProcessing) => {
    console.log("🔄 Flow processing:", isProcessing);
    setFlowProcessing(isProcessing);
    flowProcessingRef.current = isProcessing;

    // 🔥 TIMEOUT SIMPLES: 8 segundos e libera SEM PERGUNTAR
    if (isProcessing) {
      setTimeout(() => {
        console.log("⏰ TIMEOUT - Liberando campo FORÇADO");
        setFlowProcessing(false);
        flowProcessingRef.current = false;
      }, 8000);
    }
  }, []);

  const handleFlowTriggered = useCallback((data) => {
    console.log("✅ Fluxo concluído");

    // 🔥 RESET IMEDIATO - SEM TIMEOUT
    setFlowProcessing(false);
    flowProcessingRef.current = false;
  }, []);


  // NAVEGAÇÃO POR TECLADO CORRIGIDA
  const handleKeyDown = useCallback((e) => {
    // ✅ Mention autocomplete keyboard navigation
    if (showMentionList && mentionListFiltered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev < mentionListFiltered.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev > 0 ? prev - 1 : mentionListFiltered.length - 1
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const idx = selectedMentionIndex >= 0 ? selectedMentionIndex : 0;
        handleMentionSelectRef.current(mentionListFiltered[idx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionList(false);
        return;
      }
    }

    // Enter envia; Shift+Enter quebra linha. preventDefault cancela o keypress — por isso o envio é aqui via ref.
    if (e.key === "Enter" && !e.shiftKey && !isNavigatingQuickAnswers) {
      e.preventDefault();
      handleSendMessageRef.current();
    }

    if (!typeBar || !Array.isArray(typeBar) || typeBar.length === 0) {
      setSelectedQuickAnswerIndex(-1);
      setIsNavigatingQuickAnswers(false);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsNavigatingQuickAnswers(true);
        setSelectedQuickAnswerIndex(prev => {
          const nextIndex = prev < typeBar.length - 1 ? prev + 1 : 0;

          // SCROLL AUTOMÁTICO CORRIGIDO
          setTimeout(() => {
            const container = document.querySelector('.MuiBox-root ul');
            if (container) {
              const selectedElement = container.children[nextIndex];
              if (selectedElement) {
                selectedElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest'
                });
              }
            }
          }, 0);

          return nextIndex;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setIsNavigatingQuickAnswers(true);
        setSelectedQuickAnswerIndex(prev => {
          const nextIndex = prev > 0 ? prev - 1 : typeBar.length - 1;

          // SCROLL AUTOMÁTICO CORRIGIDO
          setTimeout(() => {
            const container = document.querySelector('.MuiBox-root ul');
            if (container) {
              const selectedElement = container.children[nextIndex];
              if (selectedElement) {
                selectedElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest'
                });
              }
            }
          }, 0);

          return nextIndex;
        });
        break;

      case 'Enter':
        if (isNavigatingQuickAnswers && selectedQuickAnswerIndex >= 0) {
          e.preventDefault();
          const selectedAnswer = typeBar[selectedQuickAnswerIndex];
          handleQuickAnswersClick(selectedAnswer);
          setSelectedQuickAnswerIndex(-1);
          setIsNavigatingQuickAnswers(false);
        }
        break;

      case 'Escape':
        if (isNavigatingQuickAnswers) {
          e.preventDefault();
          setSelectedQuickAnswerIndex(-1);
          setIsNavigatingQuickAnswers(false);
          setTypeBar(false);
        }
        break;

      case 'Tab':
        if (isNavigatingQuickAnswers) {
          e.preventDefault();
          setSelectedQuickAnswerIndex(prev => {
            const nextIndex = prev < typeBar.length - 1 ? prev + 1 : 0;
            return nextIndex;
          });
        }
        break;

      default:
        if (isNavigatingQuickAnswers && e.key.length === 1) {
          setSelectedQuickAnswerIndex(-1);
          setIsNavigatingQuickAnswers(false);
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleQuickAnswersClick declarado abaixo; deps completas causariam TDZ
  }, [typeBar, selectedQuickAnswerIndex, isNavigatingQuickAnswers, showMentionList, mentionListFiltered]);

  useEffect(() => {
    if (!typeBar || !Array.isArray(typeBar) || typeBar.length === 0) {
      setSelectedQuickAnswerIndex(-1);
      setIsNavigatingQuickAnswers(false);
    }
  }, [typeBar]);

  const getQuickAnswerItemStyle = (index) => ({
    backgroundColor: selectedQuickAnswerIndex === index
      ? (theme.mode === 'light' ? '#e3f2fd' : '#1e3a5f')
      : 'transparent',
    borderLeft: selectedQuickAnswerIndex === index
      ? `4px solid ${theme.palette.primary.main}`
      : '4px solid transparent',
  });

  const handleStickerSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = null;

    try {
      setLoading(true);

      // Se já for .webp, enviar direto sem conversão
      const isWebp = file.type === "image/webp" || file.name.toLowerCase().endsWith(".webp");

      let stickerBlob;
      if (isWebp) {
        stickerBlob = file;
      } else {
        // Converter para WebP 512x512 via Canvas
        stickerBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxSize = 512;
            let w = img.width, h = img.height;
            if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
            else { w = Math.round(w * maxSize / h); h = maxSize; }
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob(
              (blob) => blob ? resolve(blob) : reject(new Error("Falha na conversão")),
              "image/webp",
              0.8
            );
          };
          img.onerror = () => reject(new Error("Erro ao carregar imagem"));
          img.src = URL.createObjectURL(file);
        });
      }

      // Validar tamanho (WhatsApp limita stickers estáticos a ~100KB)
      if (stickerBlob.size > 500000) {
        toast.warn("Imagem muito grande para sticker. Tente uma imagem menor.");
        setLoading(false);
        return;
      }

      const stickerFile = new File([stickerBlob], `sticker_${Date.now()}.webp`, { type: "image/webp" });
      const formData = new FormData();
      formData.append("medias", stickerFile);
      formData.append("body", "");
      formData.append("fromMe", true);
      formData.append("isPrivate", "false");
      formData.append("isSticker", "true");

      await api.post(`/messages/${ticketId}`, formData);
      toast.success("Figurinha enviada!");
    } catch (err) {
      console.error("Erro ao enviar sticker:", err);
      toast.error("Erro ao enviar figurinha");
    } finally {
      setLoading(false);
    }
  };

  const handleSendLinkVideo = async () => {
    const link = `https://meet.jit.si/${ticketId}`;
    setInputMessage(link);
  };

  const handleSendTemplate = async () => {
    setTemplateModalOpen(true);
  };

  /** Envia indicador "digitando..." para o contato (best-effort, debounced). */
  const sendTypingPresence = useCallback((presence = "composing") => {
    if (ticketChannel !== "whatsapp") return;
    api.post(`/messages/${ticketId}/presence`, { presence }).catch(() => {});
  }, [ticketId, ticketChannel]);

  const handleChangeInput = useCallback((e) => {
    setInputMessage(e.target.value);

    // Indicador de digitação — debounce: envia "composing" ao começar,
    // e "paused" 3s após parar de digitar
    if (!isTypingRef.current && e.target.value) {
      isTypingRef.current = true;
      sendTypingPresence("composing");
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      sendTypingPresence("paused");
    }, 3000);

    // ✅ Mention detection (only in groups)
    if (ticketStatus === "group" && mentionList.length > 0) {
      const cursorPos = e.target.selectionStart;
      const textBeforeCursor = e.target.value.substring(0, cursorPos);

      // Find the last @ that's at start or preceded by space/newline
      const mentionMatch = textBeforeCursor.match(/(?:^|\s)@(\S*)$/);

      if (mentionMatch) {
        const searchTerm = mentionMatch[1].toLowerCase();
        const startPos = cursorPos - mentionMatch[1].length - 1; // position of @

        setMentionSearch(searchTerm);
        setMentionStartPos(startPos);

        // Filter participants
        const filtered = searchTerm
          ? mentionList.filter(p =>
              p.name.toLowerCase().includes(searchTerm) ||
              p.number.includes(searchTerm)
            )
          : mentionList; // Show all when just "@" is typed

        // Always include @all option at the top
        const allOption = { jid: "all", name: "Todos", number: "" };
        const withAll = [allOption, ...filtered.filter(p => p.jid !== "all")];

        setMentionListFiltered(withAll);
        setShowMentionList(withAll.length > 0);
        setSelectedMentionIndex(-1);
      } else {
        setShowMentionList(false);
        setMentionSearch("");
        setMentionStartPos(-1);
      }
    }
  }, [sendTypingPresence, ticketStatus, mentionList]);

  const handleMentionSelect = useCallback((participant) => {
    if (mentionStartPos < 0) return;

    const beforeMention = inputMessage.substring(0, mentionStartPos);
    const afterMention = inputMessage.substring(
      mentionStartPos + 1 + mentionSearch.length // +1 for @
    );

    const mentionText = participant.jid === "all" ? "@all" : `@${participant.name}`;
    const newMessage = beforeMention + mentionText + " " + afterMention;

    setInputMessage(newMessage);

    // Track the mention for sending (skip @all - handled by backend)
    if (participant.jid !== "all") {
      setSelectedMentions(prev => {
        // Avoid duplicates
        if (prev.find(m => m.jid === participant.jid)) return prev;
        return [...prev, { jid: participant.jid, name: participant.name }];
      });
    }

    setShowMentionList(false);
    setMentionSearch("");
    setMentionStartPos(-1);
    setSelectedMentionIndex(-1);

    // Focus input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const pos = (beforeMention + mentionText + " ").length;
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 50);
  }, [inputMessage, mentionStartPos, mentionSearch]);

  handleMentionSelectRef.current = handleMentionSelect;

  /** TextareaAutosize (MUI) às vezes mantém altura após `value=""`; força recálculo. */
  const resetMultilineInputHeight = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const ta =
        el.nodeName === "TEXTAREA"
          ? el
          : typeof el.querySelector === "function"
            ? el.querySelector("textarea")
            : null;
      if (ta && ta.style) {
        ta.style.height = "auto";
        ta.style.minHeight = "";
        ta.style.overflowY = "hidden";
      }
    });
  }, []);

  const handlePrivateMessage = (e) => {
    if (isTicketPending()) {
      return;
    }
    setPrivateMessage(!privateMessage);
    setPrivateMessageInputVisible(!privateMessageInputVisible);
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      case 'document': return '📎';
      default: return '📎';
    }
  };

  const getMediaTypeColor = (mediaType) => {
    switch (mediaType) {
      case 'audio': return 'secondary';
      case 'image': return 'primary';
      case 'video': return 'default';
      case 'document': return 'default';
      default: return 'default';
    }
  };

  const handleQuickAnswersClick = useCallback(async (value) => {
    console.log("🎯 handleQuickAnswersClick chamado:", value);
    console.log("📋 ticketId atual:", ticketId);
    console.log("📝 mediaName disponível:", value.mediaName);
    console.log("📝 mediaType disponível:", value.mediaType);
    console.log("📝 mediaPath disponível:", value.mediaPath);

    if (!ticketId) {
      console.error("❌ ticketId não encontrado");
      toastError("Erro: ID do ticket não encontrado");
      return;
    }

    if (value.mediaPath) {
      if (quickMessageMediaBusyRef.current) {
        console.log("⚠️ Resposta rápida com mídia já em envio, ignorando clique duplicado");
        return;
      }
      quickMessageMediaBusyRef.current = true;
      try {
        setLoading(true);
        console.log("📥 Baixando mídia:", value.mediaPath);

        // Sempre usar api.get (axios): URLs absolutas de quick message vêm do backend com
        // Authorization necessário; fetch() não envia o Bearer e o cache pode devolver HTML/401.
        const mediaUrl = value.mediaPath;
        // Sem headers extras: Cache-Control/Pragma em cross-origin (3000→8080) exigem
        // Access-Control-Allow-Headers no backend e quebram o preflight CORS.
        const response = await api.get(mediaUrl, {
          responseType: "blob",
        });

        console.log("✅ Mídia baixada com sucesso, tamanho:", response.data.size);
        console.log("📝 Nome original do arquivo:", value.mediaName);

        const messageBody = value.value && value.value.trim() !== "" ? value.value : "";

        // ✅ CORREÇÃO: Passar o nome original do arquivo se disponível
        await handleUploadQuickMessageMedia(
          response.data, 
          messageBody, 
          value.mediaType,
          value.mediaName || value.mediaName // Passar o nome original do arquivo
        );

        console.log("✅ Mídia enviada com sucesso");

        setInputMessage("");
        setTypeBar(false);
        return;
      } catch (err) {
        console.error("❌ Erro ao processar mídia:", err);
        toastError(err);
      } finally {
        setLoading(false);
        quickMessageMediaBusyRef.current = false;
      }
    } else {
      // Para mensagens de texto
      setInputMessage(value.value || "");
      setTypeBar(false);
    }
    // handleUploadQuickMessageMedia definido abaixo; ordem mantida para evitar TDZ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, privateMessage]);

  const handleUploadQuickMessageMedia = useCallback(async (blob, message, mediaType = null, originalFileName = null) => {
    console.log("📤 Iniciando upload de mídia:", {
      blobSize: blob.size,
      message,
      mediaType,
      originalFileName,
      ticketId
    });

    if (!ticketId) {
      throw new Error("ID do ticket não encontrado");
    }

    try {
      let extension = 'bin';
      let filename = null;

      // ✅ CORREÇÃO: Se temos o nome original, usar ele e extrair a extensão
      if (originalFileName) {
        const lastDot = originalFileName.lastIndexOf('.');
        if (lastDot !== -1) {
          extension = originalFileName.substring(lastDot + 1).toLowerCase();
          filename = originalFileName;
        } else {
          filename = originalFileName;
        }
      }

      // Se não temos nome original, detectar extensão do tipo MIME
      if (!filename) {
        if (blob.type) {
          // Mapear tipos MIME comuns para extensões
          const mimeToExt = {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/msword': 'doc',
            'application/pdf': 'pdf',
            'application/vnd.ms-excel': 'xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
            'application/vnd.ms-powerpoint': 'ppt',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
            'image/jpeg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'video/mp4': 'mp4',
            'video/quicktime': 'mov',
            'audio/mpeg': 'mp3',
            'audio/wav': 'wav',
            'audio/webm': 'webm',
            'audio/ogg': 'ogg'
          };

          extension = mimeToExt[blob.type] || blob.type.split("/")[1]?.split(";")[0] || 'bin';

          // Limpar extensão se for muito longa (tipo MIME completo)
          if (extension.length > 10) {
            if (blob.type.includes('webm') || blob.type.includes('audio')) {
              extension = blob.type.includes('webm') ? 'webm' : 'mp3';
            } else if (blob.type.includes('image')) {
              extension = 'jpg';
            } else if (blob.type.includes('video')) {
              extension = 'mp4';
            } else if (blob.type.includes('document') || blob.type.includes('word')) {
              extension = 'docx';
            } else {
              extension = 'bin';
            }
          }
        } else if (mediaType) {
          const typeExtensionMap = {
            'audio': 'webm',
            'image': 'jpg',
            'video': 'mp4',
            'document': 'docx' // ✅ CORREÇÃO: mudado de 'pdf' para 'docx' como padrão
          };
          extension = typeExtensionMap[mediaType] || 'bin';
        }

        filename = `${new Date().getTime()}.${extension}`;
      }

      const formData = new FormData();
      // ✅ CORREÇÃO: Criar um File object com o nome correto para preservar originalname
      // O nome do arquivo no File object será usado como originalname pelo multer
      const file = new File([blob], filename, { 
        type: blob.type || (mediaType === 'document' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : '')
      });
      
      console.log("📎 File object criado:", {
        name: file.name,
        size: file.size,
        type: file.type,
        originalFileName: originalFileName
      });
      
      formData.append("medias", file); // Não precisa do terceiro parâmetro, o nome já está no File
      formData.append("typeArch", "quickMessage");

      const pending = ticketStatus === "pending";
      const body = message && message.trim() !== ""
        ? ((privateMessage || pending) ? `\u200d${message}` : message)
        : ((privateMessage || pending) ? `\u200d` : "");

      formData.append("body", body);
      formData.append("fromMe", true);
      formData.append("isPrivate", (privateMessage || pending) ? "true" : "false");

      const isAudioQuick =
        mediaType === "audio" ||
        (blob.type && String(blob.type).startsWith("audio/")) ||
        /\.(mp3|ogg|webm|m4a|wav|opus)$/i.test(String(filename));
      if (isAudioQuick) {
        const durationSeconds = await getAudioDurationSeconds(blob);
        formData.append("audioDurationSeconds", String(durationSeconds));
      }

      console.log("📤 Enviando para:", `/messages/${ticketId}`);

      if (isMounted.current) {
        const response = await api.post(`/messages/${ticketId}`, formData);
        console.log("✅ Upload realizado com sucesso:", response.status);
        
        // ✅ Manter foco no input após enviar mídia de mensagem rápida
        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      console.error("❌ Erro no upload:", err);
      // ✅ Não mostrar toastError se for erro de janela de 24h
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
      throw err;
    }
  }, [ticketId, privateMessage, ticketStatus]);

  // Flag para indicar que o foco veio de uma seleção de emoji
  const emojiFocusRef = useRef(false);
  
  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
    
    // NÃO fechar o modal - permitir que o usuário selecione vários emojis
    // Apenas focar o input para que o usuário possa pressionar Enter para enviar
    // O modal só fecha quando:
    // 1. O usuário enviar a mensagem (Enter ou botão enviar)
    // 2. O usuário clicar manualmente no input (não quando o foco vier de emoji)
    
    // Marcar que o foco está vindo de uma seleção de emoji
    emojiFocusRef.current = true;
    
    // Focar o input após um pequeno delay para garantir que o estado foi atualizado
    // Usar requestAnimationFrame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Mover o cursor para o final do texto
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
        // Resetar a flag após um pequeno delay
        setTimeout(() => {
          emojiFocusRef.current = false;
        }, 100);
      }, 50);
    });
  };

  const [modalCameraOpen, setModalCameraOpen] = useState(false);

  const handleCapture = (blob) => {
    if (!blob) return;
    if (blob.type && blob.type.startsWith("video/")) {
      handleUploadWebcamVideo(blob);
    } else {
      handleUploadCamera(blob);
    }
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }
    const selectedMedias = Array.from(e.target.files);
    setMediasUpload(selectedMedias);
    setShowModalMedias(true);
  };

  const handleChangeSign = (e) => {
    getStatusSingMessageLocalstogare();
  };

  const handleOpenModalForward = () => {
    if (selectedMessages.length === 0) {
      setForwardMessageModalOpen(false);
      toastError(i18n.t("messagesList.header.notMessage"));
      return;
    }
    setForwardMessageModalOpen(true);
  };

  const getStatusSingMessageLocalstogare = () => {
    const signMessageStorage = JSON.parse(
      localStorage.getItem("persistentSignMessage")
    );
    if (signMessageStorage !== null) {
      if (signMessageStorage) {
        localStorage.setItem("persistentSignMessage", false);
        setSignMessage(false);
      } else {
        localStorage.setItem("persistentSignMessage", true);
        setSignMessage(true);
      }
    } else {
      localStorage.setItem("persistentSignMessage", false);
      setSignMessage(false);
    }
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      const selectedMedias = Array.from(e.clipboardData.files);
      setMediasUpload(selectedMedias);
      setShowModalMedias(true);
    }
  };

  const handleInputDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) {
      const selectedMedias = Array.from(e.dataTransfer.files);
      setMediasUpload(selectedMedias);
      setShowModalMedias(true);
    }
  };

  const handleUploadMedia = async (mediasUpload) => {
    if (needsTemplate && isWhatsAppOfficial && !isTicketPending()) {
      toastError("Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa.");
      return;
    }

    if (!mediasUpload.length) {
      return;
    }

    const optimisticIds = [];
    for (const media of mediasUpload) {
      const clientTempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      optimisticIds.push(clientTempId);
      const blobUrl = URL.createObjectURL(media.file);
      const mediaType = inferMediaTypeFromFile(media.file);
      window.dispatchEvent(
        new CustomEvent("multiflow-optimistic-message", {
          detail: {
            ticketDbId: ticketId,
            clientTempId,
            message: {
              id: clientTempId,
              clientTempId,
              optimistic: true,
              mediaKind: "media",
              body: media.caption || "",
              fromMe: true,
              read: true,
              ack: 0,
              mediaUrl: blobUrl,
              mediaType,
              mediaName: media.file.name,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              ticketId: ticketId,
              quotedMsg: null,
              isPrivate: !!(privateMessage || isTicketPending()),
            },
          },
        })
      );
    }

    const formData = new FormData();
    formData.append("fromMe", true);
    formData.append("isPrivate", (privateMessage || isTicketPending()) ? "true" : "false");
    mediasUpload.forEach((media) => {
      formData.append("body", media.caption);
      formData.append("medias", media.file);
    });

    // Igual ao envio de texto com otimista: liberar a barra de input enquanto o POST corre em background.
    // O modal já chamou onClose (showModalMedias false); sem limpar mediasUpload o layout ficava só com um Paper vazio.
    setMediasUpload([]);
    setShowModalMedias(false);
    if (!isTicketPending()) {
      setPrivateMessage(false);
      setPrivateMessageInputVisible(false);
    }
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      optimisticIds.forEach((clientTempId) => {
        window.dispatchEvent(
          new CustomEvent("multiflow-optimistic-message-error", {
            detail: { ticketDbId: ticketId, clientTempId },
          })
        );
      });
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
    }
  };

  const handleSendContatcMessage = async (vcard) => {
    setSenVcardModalOpen(false);
    setLoading(true);

    if (isNil(vcard)) {
      setLoading(false);
      return;
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: null,
      quotedMsg: replyingMessage,
      isPrivate: (privateMessage || isTicketPending()) ? "true" : "false",
      vCard: vcard,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
      
      // ✅ Limpar campos apenas se envio foi bem sucedido
      setInputMessage("");
      clearDraftForTicket(ticketId, draftTimerRef); // [DRAFT] limpar rascunho após envio
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      setEditingMessage(null);
      if (!isTicketPending()) {
        setPrivateMessage(false);
        setPrivateMessageInputVisible(false);
      }

      // ✅ Manter foco no input após enviar contato
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } catch (err) {
      // ✅ Não mostrar toastError se for erro de janela de 24h
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage || inputMessage.trim() === "") return;

    // ✅ VALIDAÇÃO: Impedir envio se precisa de template
    if (needsTemplate && isWhatsAppOfficial && !isTicketPending()) {
      toastError("Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa.");
      return;
    }

    // Parar indicador de digitação ao enviar
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingPresence("paused");
    }

    setLoading(true);

    const userName = (privateMessage || isTicketPending())
      ? `${user.name} - Mensagem Interna`
      : user.name;

    const sendMessage = inputMessage.trim();

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body:
        ((signMessage && !isTicketPending()) || privateMessage || isTicketPending())
          ? `*${userName}:*\n${sendMessage}`
          : sendMessage,
      quotedMsg: replyingMessage,
      isPrivate: (privateMessage || isTicketPending()) ? "true" : "false",
    };
    if (pendingInteractivePayload) {
      if (pendingInteractivePayload.type === "button" && Array.isArray(pendingInteractivePayload.buttons)) {
        message.buttons = pendingInteractivePayload.buttons;
      }
      if (pendingInteractivePayload.type === "list" && Array.isArray(pendingInteractivePayload.sections)) {
        message.sections = pendingInteractivePayload.sections;
        if (pendingInteractivePayload.buttonText) message.buttonText = pendingInteractivePayload.buttonText;
      }
    }

    // ✅ Add mentions to message payload
    if (selectedMentions.length > 0) {
      message.mentions = selectedMentions.map(m => m.jid);
    }

    optimisticClientTempIdRef.current = null;
    try {
      const useOptimistic = !pendingInteractivePayload;
      if (useOptimistic) {
        const clientTempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        optimisticClientTempIdRef.current = clientTempId;
        window.dispatchEvent(
          new CustomEvent("multiflow-optimistic-message", {
            detail: {
              ticketDbId: ticketId,
              clientTempId,
              message: {
                id: clientTempId,
                clientTempId,
                optimistic: true,
                body: message.body,
                fromMe: true,
                read: true,
                ack: 0,
                mediaUrl: "",
                mediaType: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                ticketId: ticketId,
                quotedMsg: replyingMessage || null,
                isPrivate: message.isPrivate === "true",
              },
            },
          })
        );
        setInputMessage("");
        clearDraftForTicket(ticketId, draftTimerRef); // [DRAFT] limpar rascunho após envio otimista
        resetMultilineInputHeight();
        setPendingInteractivePayload(null);
        setShowEmoji(false);
        setReplyingMessage(null);
        setSelectedMentions([]);
        if (!isTicketPending()) {
          setPrivateMessage(false);
        }
        if (!isTicketPending()) {
          setPrivateMessageInputVisible(false);
        }
        handleMenuItemClick();
        setLoading(false);
        setTimeout(() => {
          resetMultilineInputHeight();
          inputRef.current?.focus();
        }, 50);
      }
      await api.post(`/messages/${ticketId}`, message);
      optimisticClientTempIdRef.current = null;
      if (!useOptimistic) {
        setInputMessage("");
        clearDraftForTicket(ticketId, draftTimerRef); // [DRAFT] limpar rascunho após envio não-otimista
        resetMultilineInputHeight();
        setPendingInteractivePayload(null);
        setShowEmoji(false);
        setLoading(false);
        setReplyingMessage(null);
        setSelectedMentions([]);
        if (!isTicketPending()) {
          setPrivateMessage(false);
        }
        if (!isTicketPending()) {
          setPrivateMessageInputVisible(false);
        }
        handleMenuItemClick();
        setTimeout(() => {
          resetMultilineInputHeight();
          inputRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      const failedOptimisticId = optimisticClientTempIdRef.current;
      optimisticClientTempIdRef.current = null;
      if (failedOptimisticId) {
        window.dispatchEvent(
          new CustomEvent("multiflow-optimistic-message-error", {
            detail: { ticketDbId: ticketId, clientTempId: failedOptimisticId },
          })
        );
      }
      // ✅ Não mostrar toastError se for erro de janela de 24h
      // O usuário já vê o alerta visual
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
      setLoading(false);
    }
  };

  handleSendMessageRef.current = handleSendMessage;

  const submitEditMessage = async (body) => {
    if (ticketChannel === "whatsapp_oficial") {
      toast.warn(i18n.t("messageOptionsMenu.officialApiNoEditDelete"));
      setEditingMessage(null);
      return;
    }
    if (!editingMessage) return;
    setLoading(true);
    try {
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body,
        quotedMsg: replyingMessage,
        isPrivate: (privateMessage || isTicketPending()) ? "true" : "false",
      };
      await api.post(`/messages/edit/${editingMessage.id}`, message);
      setEditingMessage(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessageTemplate = async (e) => {
    if (e.id === "") return;
    setLoading(true);

    const message = {
      templateId: e.id,
      variables: e.variables,
      bodyToSave: e.bodyToSave,
      mediaUrl: "",
      quotedMsg: replyingMessage,
    };

    try {
      await api.post(`/messages-template/${ticketId}`, message);
      
      // ✅ A janela de 24h só abre quando o contato RESPONDE ao template
      // Não resetar needsTemplate aqui - será atualizado quando receber resposta em ReceivedWhatsApp.ts
      console.log("✅ Template enviado - Aguardando resposta do contato para abrir janela de 24h");
      
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
    setTemplateModalOpen(false);
    setInputMessage("");
    clearDraftForTicket(ticketId, draftTimerRef); // [DRAFT] limpar rascunho após envio de template
    setShowEmoji(false);
    setReplyingMessage(null);
    if (!isTicketPending()) {
      setPrivateMessage(false);
    }
    setEditingMessage(null);
    if (!isTicketPending()) {
      setPrivateMessageInputVisible(false);
    }
    handleMenuItemClick();
    
    // ✅ Manter foco no input após enviar template
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      // Respostas rápidas (/) devem mostrar só as cadastradas no sistema, nunca templates da API Oficial
      const messages = await listQuickMessages({
        companyId,
        userId: user.id,
        isOficial: "false",
        onlyRespostasRapidas: true,
      });
      const options = messages.map((m) => {
        let truncatedMessage = m.message;
        if (isString(truncatedMessage) && truncatedMessage.length > 90) {
          truncatedMessage = m.message.substring(0, 90) + "...";
        }
        return {
          value: m.message,
          label: `/${m.shortcode} - ${truncatedMessage}`,
          mediaPath: m.mediaPath,
          mediaName: m.mediaName || null, // ✅ CORREÇÃO: Incluir mediaName
          mediaType: m.mediaType,
          shortcode: m.shortcode,
        };
      });
      if (isMounted.current) {
        setQuickAnswer(options);
      }
    }
    fetchData();
    // listQuickMessages é recriado a cada render em useQuickMessages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.companyId, user.id]);

  useEffect(() => {
    if (
      isString(inputMessage) &&
      !isEmpty(inputMessage) &&
      inputMessage.length >= 1
    ) {
      const firstWord = inputMessage.charAt(0);

      if (firstWord === "/") {
        setTypeBar(firstWord.indexOf("/") > -1);

        const filteredOptions = quickAnswers.filter(
          (m) => m.label.toLowerCase().indexOf(inputMessage.toLowerCase()) > -1
        );
        setTypeBar(filteredOptions);
      } else {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  }, [inputMessage, quickAnswers]);

  useEffect(() => {
    console.log("🔍 Modal state:", triggerFlowModalOpen, "Flow processing:", flowProcessing);

    // 🔥 RESET SIMPLES: Se modal fecha, libera campo
    if (!triggerFlowModalOpen && flowProcessing) {
      console.log("🔓 FORÇANDO liberação do campo");
      setFlowProcessing(false);
      flowProcessingRef.current = false;
    }
  }, [triggerFlowModalOpen, flowProcessing]);

  const disableOption = useCallback(() => {
    const isFlowProcessing = flowProcessingRef.current || flowProcessing;

    return (
      loading ||
      recording ||
      isFlowProcessing || // 🛡️ Usar tanto ref quanto estado
      Boolean(editingMessage) ||
      needsTemplate || // ✅ Desabilitar quando precisa de template
      (ticketStatus !== "pending" && ticketStatus !== "open" && ticketStatus !== "group")
    );
  }, [loading, recording, flowProcessing, ticketStatus, needsTemplate, editingMessage]);

  const disableOptionForPending = useCallback(() => {
    const isFlowProcessing = flowProcessingRef.current || flowProcessing;

    return (
      loading ||
      recording ||
      isFlowProcessing || // 🛡️ Usar tanto ref quanto estado
      Boolean(editingMessage) ||
      ticketStatus === "closed"
    );
  }, [loading, recording, flowProcessing, ticketStatus, editingMessage]);

  const handleUploadCamera = async (blob) => {
    if (needsTemplate && isWhatsAppOfficial && !isTicketPending()) {
      toastError("Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa.");
      return;
    }

    const clientTempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const blobUrl = URL.createObjectURL(blob);
    window.dispatchEvent(
      new CustomEvent("multiflow-optimistic-message", {
        detail: {
          ticketDbId: ticketId,
          clientTempId,
          message: {
            id: clientTempId,
            clientTempId,
            optimistic: true,
            mediaKind: "media",
            body: (privateMessage || isTicketPending()) ? "\u200d" : "",
            fromMe: true,
            read: true,
            ack: 0,
            mediaUrl: blobUrl,
            mediaType: "image",
            mediaName: "camera.png",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ticketId: ticketId,
            quotedMsg: null,
            isPrivate: !!(privateMessage || isTicketPending()),
          },
        },
      })
    );

    try {
      const formData = new FormData();
      const filename = `${new Date().getTime()}.png`;
      formData.append("medias", blob, filename);
      formData.append("body", (privateMessage || isTicketPending()) ? `\u200d` : "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("multiflow-optimistic-message-error", {
          detail: { ticketDbId: ticketId, clientTempId },
        })
      );
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
    }
  };

  const handleUploadWebcamVideo = async (blob) => {
    if (needsTemplate && isWhatsAppOfficial && !isTicketPending()) {
      toastError("Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa.");
      return;
    }

    const ext = blob.type && blob.type.includes("mp4") ? "mp4" : "webm";
    const filename = `${new Date().getTime()}.${ext}`;
    const clientTempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const blobUrl = URL.createObjectURL(blob);
    window.dispatchEvent(
      new CustomEvent("multiflow-optimistic-message", {
        detail: {
          ticketDbId: ticketId,
          clientTempId,
          message: {
            id: clientTempId,
            clientTempId,
            optimistic: true,
            mediaKind: "media",
            body: (privateMessage || isTicketPending()) ? "\u200d" : "",
            fromMe: true,
            read: true,
            ack: 0,
            mediaUrl: blobUrl,
            mediaType: "video",
            mediaName: filename,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ticketId: ticketId,
            quotedMsg: null,
            isPrivate: !!(privateMessage || isTicketPending()),
          },
        },
      })
    );

    try {
      const formData = new FormData();
      const videoFile = new File([blob], filename, {
        type: blob.type || (ext === "mp4" ? "video/mp4" : "video/webm"),
      });
      formData.append("medias", videoFile);
      formData.append("body", (privateMessage || isTicketPending()) ? `\u200d` : "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("multiflow-optimistic-message-error", {
          detail: { ticketDbId: ticketId, clientTempId },
        })
      );
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
    }
  };

  // ✅ CORREÇÃO KISS: Apenas otimizar nome do arquivo para mobile
  const handleUploadAudio = async () => {
    if (needsTemplate && isWhatsAppOfficial && !isTicketPending()) {
      toastError("Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa.");
      return;
    }

    setLoading(true);
    let optimisticAudioTempId = null;
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();

      // Mp3Recorder.getMp3() sempre retorna áudio em MP3 (audio/mpeg). Usar .mp3 em todos os dispositivos
      // para evitar rejeição na API Oficial (mobile falhava ao enviar .ogg com conteúdo MP3).
      let filename;
      if (["whatsapp", "whatsapp_oficial"].includes(ticketChannel)) {
        filename = `audio_${new Date().getTime()}.mp3`;
      } else {
        filename = `${new Date().getTime()}.m4a`;
      }

      // Garantir mimetype explícito (em alguns mobile o blob.type pode vir vazio)
      const audioFile = new File([blob], filename, { type: blob.type || "audio/mpeg" });

      optimisticAudioTempId = `opt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const blobUrl = URL.createObjectURL(audioFile);
      window.dispatchEvent(
        new CustomEvent("multiflow-optimistic-message", {
          detail: {
            ticketDbId: ticketId,
            clientTempId: optimisticAudioTempId,
            message: {
              id: optimisticAudioTempId,
              clientTempId: optimisticAudioTempId,
              optimistic: true,
              mediaKind: "media",
              body: "",
              fromMe: true,
              read: true,
              ack: 0,
              mediaUrl: blobUrl,
              mediaType: "audio",
              mediaName: filename,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              ticketId: ticketId,
              quotedMsg: null,
              isPrivate: !!(privateMessage || isTicketPending()),
            },
          },
        })
      );

      // Sai do modo "gravando" assim que o áudio entra no ticket (otimista); o envio HTTP continua em background.
      // Antes: setLoading(false) aqui + recording só no finally → gravador ficava visível vários segundos durante upload.
      setRecording(false);

      formData.append("medias", audioFile);
      formData.append("body", "");
      formData.append("fromMe", true);
      formData.append("isPrivate", (privateMessage || isTicketPending()) ? "true" : "false");

      const durationSeconds = await getAudioDurationSeconds(blob);
      formData.append("audioDurationSeconds", String(durationSeconds));

      console.log(`📤 Enviando áudio: ${filename} (${blob.size} bytes, ${durationSeconds}s)`);

      if (isMounted.current) {
        await api.post(`/messages/${ticketId}`, formData);

        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      if (optimisticAudioTempId) {
        window.dispatchEvent(
          new CustomEvent("multiflow-optimistic-message-error", {
            detail: { ticketDbId: ticketId, clientTempId: optimisticAudioTempId },
          })
        );
      }
      const errorMessage = err?.response?.data?.error || err?.message || "";
      if (!errorMessage.includes("Janela de 24h")) {
        toastError(err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRecording(false);
      }
    }
  };

  const handleCloseModalMedias = () => {
    setShowModalMedias(false);
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (event) => {
    setAnchorEl(null);
  };

  const handleSendContactModalOpen = async () => {
    handleMenuItemClick();
    setSenVcardModalOpen(true);
  };

  const handleCameraModalOpen = async () => {
    handleMenuItemClick();
    if (useNativeCameraCapture) {
      window.setTimeout(() => {
        cameraCaptureInputRef.current?.click();
      }, 0);
      return;
    }
    setModalCameraOpen(true);
  };

  const handleCancelSelection = () => {
    setMediasUpload([]);
    setShowModalMedias(false);
  };

  const checkForSelectedText = useCallback(() => {
    if (inputRef.current) {
      const start = inputRef.current.selectionStart;
      const end = inputRef.current.selectionEnd;

      if (start !== end && start !== null && end !== null) {
        const selectedText = inputMessage.substring(start, end);
        if (selectedText.trim() !== '') {
          // Para InputBase, calcular posição baseada no elemento
          const inputRect = inputRef.current.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          setSelectedText({
            text: selectedText,
            start: start,
            end: end
          });

          setFormatMenuAnchorPosition({
            x: inputRect.left + inputRect.width / 2,
            y: inputRect.top + scrollTop - 10 // Posicionar acima do input
          });

          return true;
        }
      }
    }

    setFormatMenuAnchorPosition(null);
    return false;
  }, [inputMessage]);

  const handleCloseFormatMenu = useCallback(() => {
    setFormatMenuAnchorPosition(null);
  }, []);

  // Aplica a formatação ao texto selecionado
  const handleFormatText = useCallback((formatType) => {
    const { text, start, end } = selectedText;
    let formattedText = '';

    switch (formatType) {
      case 'bold':
        formattedText = `*${text}*`;
        break;
      case 'italic':
        formattedText = `_${text}_`;
        break;
      case 'strikethrough':
        formattedText = `~${text}~`;
        break;
      case 'code':
        formattedText = `\`${text}\``;
        break;
      case 'numberedList':
        formattedText = text.split('\n')
          .map((line, index) => `${index + 1}. ${line}`)
          .join('\n');
        break;
      case 'bulletList':
        formattedText = text.split('\n')
          .map(line => `• ${line}`)
          .join('\n');
        break;
      case 'quote':
        formattedText = text.split('\n')
          .map(line => `> ${line}`)
          .join('\n');
        break;
      case 'clear':
        formattedText = text
          .replace(/\*([^*]+)\*/g, '$1')  // remove negrito
          .replace(/_([^_]+)_/g, '$1')    // remove itálico
          .replace(/~([^~]+)~/g, '$1')    // remove tachado
          .replace(/`([^`]+)`/g, '$1')    // remove código
          .replace(/^\d+\.\s/gm, '')      // remove numeração de lista
          .replace(/^•\s/gm, '')          // remove marcadores de lista
          .replace(/^>\s/gm, '');         // remove citação
        break;
      default:
        formattedText = text;
    }

    // Substitui o texto selecionado pelo texto formatado
    const newInputMessage =
      inputMessage.substring(0, start) +
      formattedText +
      inputMessage.substring(end);

    setInputMessage(newInputMessage);

    // Fecha o menu após a formatação
    handleCloseFormatMenu();

    // Define o foco e a posição do cursor após a operação
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursorPosition = start + formattedText.length;
        inputRef.current.selectionStart = newCursorPosition;
        inputRef.current.selectionEnd = newCursorPosition;
      }
    }, 100);
  }, [selectedText, inputMessage, handleCloseFormatMenu]);

  // Handlers para detectar seleção de texto
  const handleSelectText = useCallback(() => {
    checkForSelectedText();
  }, [checkForSelectedText]);

  const handleMouseUp = useCallback(() => {
    checkForSelectedText();
  }, [checkForSelectedText]);

  const handleKeyUp = useCallback((e) => {
    // Teclas que podem alterar a seleção
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Shift') {
      checkForSelectedText();
    }
  }, [checkForSelectedText]);

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          {replyingMessage && (
            <div className={classes.replyginMsgBody}>
              {!message.fromMe && (
                <span className={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}
              {message.body}
            </div>
          )}
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={disableOptionForPending()}
          onClick={() => {
            setReplyingMessage(null);
            setEditingMessage(null);
            setInputMessage("");
          }}
        >
          <Clear className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const renderFlowProcessingAlert = () => {
    if (!flowProcessing) return null;

    return (
      <Box className={classes.pendingAlert} style={{ backgroundColor: "#E8F5E8", borderColor: "#4CAF50" }}>
        <CircularProgress size={16} style={{ marginRight: 8 }} />
        <span>
          <strong>Fluxo em Execução:</strong> Campo de mensagem temporariamente desabilitado.
        </span>
      </Box>
    );
  };

  const renderTemplateRequiredAlert = () => {
    if (!needsTemplate || !isWhatsAppOfficial) return null;

    return (
      <Box className={classes.pendingAlert} style={{ backgroundColor: "#FFF3E0", borderColor: "#FF9800" }}>
        <WhatsApp style={{ fontSize: 20, color: "#FF9800" }} />
        <span>
          <strong>Janela de 24h Expirada:</strong> Para retomar a conversa, envie um template Meta primeiro.
        </span>
      </Box>
    );
  };

  const TextFormatMenu = () => {
    const isMenuOpen = Boolean(formatMenuAnchorPosition);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!isMenuOpen) return null;

    return (
      <ClickAwayListener onClickAway={handleCloseFormatMenu}>
        <Fade in={isMenuOpen}>
          <div
            className={classes.floatingFormatMenu}
            style={{
              top: formatMenuAnchorPosition ? formatMenuAnchorPosition.y - 50 : 0,
              left: formatMenuAnchorPosition ? formatMenuAnchorPosition.x : 0,
            }}
          >
            <Tooltip title="Negrito">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('bold')}
                size="small"
              >
                <FormatBoldIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Itálico">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('italic')}
                size="small"
              >
                <FormatItalicIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Tachado">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('strikethrough')}
                size="small"
              >
                <FormatStrikethroughIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Código">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('code')}
                size="small"
              >
                <CodeIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Lista Numerada">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('numberedList')}
                size="small"
              >
                <FormatListNumberedIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Lista com Marcadores">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('bulletList')}
                size="small"
              >
                <FormatListBulletedIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Citação">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('quote')}
                size="small"
              >
                <FormatQuoteIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Limpar Formatação">
              <IconButton
                className={classes.formatIconButton}
                disabled={disableOptionForPending()}
                onClick={() => handleFormatText('clear')}
                size="small"
              >
                <FormatClearIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </div>
        </Fade>
      </ClickAwayListener>
    );
  };

  const renderPendingAlert = () => {
    if (!isTicketPending()) return null;

    return (
      <Box className={classes.pendingAlert}>
        <Info style={{ fontSize: 20 }} />
        <span>
          <strong>Ticket Aguardando:</strong> Apenas mensagens internas são permitidas neste momento.
        </span>
      </Box>
    );
  };

  if (mediasUpload.length > 0) {
    return (
      <Paper
        elevation={0}
        square
        className={classes.viewMediaInputWrapper}
        onDragEnter={() => setOnDragEnter(true)}
        onDrop={(e) => handleInputDrop(e)}
      >
        {showModalMedias && (
          <MessageUploadMedias
            isOpen={showModalMedias}
            files={mediasUpload}
            onClose={handleCloseModalMedias}
            onSend={handleUploadMedia}
            onCancelSelection={handleCancelSelection}
          />
        )}
      </Paper>
    );
  } else {
    return (
      <>
        {templateModalOpen && (
          <TemplateModal
            open={templateModalOpen}
            handleClose={() => setTemplateModalOpen(false)}
            onSelectTemplate={(e) => handleSendMessageTemplate(e)}
            templates={templates}
          />
        )}
        <input
          ref={cameraCaptureInputRef}
          type="file"
          accept="image/*"
          capture="user"
          className={classes.uploadInput}
          tabIndex={-1}
          aria-hidden
          disabled={disableOption()}
          onChange={(e) => {
            handleChangeMedias(e);
            e.target.value = "";
          }}
        />
        {modalCameraOpen && (
          <CameraModal
            isOpen={modalCameraOpen}
            onRequestClose={() => setModalCameraOpen(false)}
            onCapture={handleCapture}
            onError={(err) =>
              toastError(err?.message || err || i18n.t("messageInput.camera.accessError"))
            }
          />
        )}
        {senVcardModalOpen && (
          <ContactSendModal
            modalOpen={senVcardModalOpen}
            onClose={(c) => {
              handleSendContatcMessage(c);
            }}
          />
        )}

        {/* NOVO MODAL DE TRIGGER FLOW */}
        {triggerFlowModalOpen && (
          <TriggerFlowModal
            open={triggerFlowModalOpen}
            onClose={handleTriggerFlowClose}
            ticketId={ticketId}
            ticketStatus={ticketStatus}
            onFlowTriggered={handleFlowTriggered}
            onFlowProcessing={handleFlowProcessing}
          />
        )}

        <EditMessageModal
          open={Boolean(editingMessage)}
          message={editingMessage}
          loading={loading}
          onClose={() => setEditingMessage(null)}
          onSubmit={submitEditMessage}
        />

        <Paper
          square
          elevation={0}
          className={classes.mainWrapper}
          onDragEnter={() => setOnDragEnter(true)}
          onDrop={(e) => handleInputDrop(e)}
        >
          {renderPendingAlert()}
          {renderFlowProcessingAlert()}
          {renderTemplateRequiredAlert()}

          {replyingMessage && renderReplyingMessage(replyingMessage)}
          <div className={classes.newMessageBox}>
            {/* Botão de Template destacado quando janela de 24h expirar */}
            {needsTemplate && isWhatsAppOfficial && (
              <Tooltip title="Enviar Template Meta (Obrigatório)">
                <IconButton
                  aria-label="send-template-required"
                  component="span"
                  onClick={handleSendTemplate}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    marginRight: 8,
                    animation: 'pulse 2s infinite'
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Tooltip>
            )}

            {!isTicketPending() && (
              <Hidden only={["sm", "xs"]}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={disableOption()}
                  onClick={(e) => setShowEmoji((prevState) => !prevState)}
                >
                  <Mood className={classes.sendMessageIcons} />
                </IconButton>
                {showEmoji ? (
                  <div className={classes.emojiBox}>
                    <ClickAwayListener 
                      onClickAway={(e) => {
                        // Não fechar se o clique foi no input ou em elementos relacionados
                        if (inputRef.current && (
                          inputRef.current.contains(e.target) || 
                          e.target === inputRef.current ||
                          inputRef.current.parentElement?.contains(e.target)
                        )) {
                          return;
                        }
                        // Fechar apenas se clicar realmente fora
                        setShowEmoji(false);
                      }}
                    >
                      <Picker
                        perLine={16}
                        theme={"dark"}
                        i18n={i18n}
                        showPreview={true}
                        showSkinTones={false}
                        onSelect={handleAddEmoji}
                      />
                    </ClickAwayListener>
                  </div>
                ) : null}

                <Fab
                  disabled={disableOption()}
                  aria-label="uploadMedias"
                  component="span"
                  className={classes.invertedFabMenu}
                  onClick={handleOpenMenuClick}
                >
                  <AddIcon />
                </Fab>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuItemClick}
                  id="simple-menu"
                >
                  <MenuItem onClick={() => { setPixModalOpen(true); handleMenuItemClick(); }}>
                    <Fab className={classes.invertedFabMenuMP} style={{ color: "#00BFA5" }}>
                      <PixIcon />
                    </Fab>
                    PIX
                  </MenuItem>
                  <MenuItem onClick={handleMenuItemClick}>
                    <input
                      multiple
                      type="file"
                      id="upload-img-button"
                      accept="image/*, video/*, audio/* "
                      className={classes.uploadInput}
                      onChange={handleChangeMedias}
                    />
                    <label htmlFor="upload-img-button">
                      <Fab
                        aria-label="upload-img"
                        component="span"
                        className={classes.invertedFabMenuMP}
                      >
                        <PermMedia />
                      </Fab>
                      {i18n.t("messageInput.type.imageVideo")}
                    </label>
                  </MenuItem>
                  <MenuItem onClick={handleCameraModalOpen}>
                    <Fab className={classes.invertedFabMenuCamera}>
                      <CameraAlt />
                    </Fab>
                    {i18n.t("messageInput.type.cam")}
                  </MenuItem>
                  <MenuItem onClick={handleMenuItemClick}>
                    <input
                      multiple
                      type="file"
                      id="upload-doc-button"
                      accept="application/*, text/*, .odt, .ods, .odp, .odg, .xml, .ofx, .zip, .rar, .7z, .tar, .gz, .bz2, .msg, .key, .numbers, .pages"
                      className={classes.uploadInput}
                      onChange={handleChangeMedias}
                    />
                    <label htmlFor="upload-doc-button">
                      <Fab
                        aria-label="upload-img"
                        component="span"
                        className={classes.invertedFabMenuDoc}
                      >
                        <Description />
                      </Fab>
                      Documento
                    </label>
                  </MenuItem>
                  <MenuItem onClick={handleSendContactModalOpen}>
                    <Fab className={classes.invertedFabMenuCont}>
                      <Person />
                    </Fab>
                    {i18n.t("messageInput.type.contact")}
                  </MenuItem>
                  <MenuItem onClick={handleSendLinkVideo}>
                    <Fab className={classes.invertedFabMenuMeet}>
                      <Duo />
                    </Fab>
                    {i18n.t("messageInput.type.meet")}
                  </MenuItem>
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setStickerPickerOpen(true); handleMenuItemClick(); }}>
                      <Fab
                        className={classes.invertedFabMenuCont}
                        style={{ color: "#FF9800" }}
                      >
                        <span role="img" aria-label="sticker" style={{ fontSize: 22 }}>🏷️</span>
                      </Fab>
                      Figurinhas
                    </MenuItem>
                  )}
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setLocationModalOpen(true); handleMenuItemClick(); }}>
                      <Fab className={classes.invertedFabMenuCont} style={{ color: "#4CAF50" }}>
                        <LocationOnIcon />
                      </Fab>
                      Localização
                    </MenuItem>
                  )}
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setPollModalOpen(true); handleMenuItemClick(); }}>
                      <Fab className={classes.invertedFabMenuCont} style={{ color: "#7C4DFF" }}>
                        <PollIcon />
                      </Fab>
                      Enquete
                    </MenuItem>
                  )}
                  {useWhatsappOfficial &&
                    ticketChannel === "whatsapp_oficial" && (
                      <MenuItem onClick={handleSendTemplate}>
                        <Fab
                          className={classes.invertedFabMenuMeet}
                          style={needsTemplate ? {
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff'
                          } : {}}
                        >
                          <WhatsApp />
                        </Fab>
                        {i18n.t("messageInput.type.template")}
                        {needsTemplate && <span style={{ marginLeft: 8, color: '#FF9800' }}>⚠️</span>}
                      </MenuItem>
                    )}
                </Menu>

                {signMessagePar && (
                  <Tooltip title={i18n.t("messageInput.tooltip.signature")}>
                    <IconButton
                      aria-label="send-upload"
                      component="span"
                      onClick={handleChangeSign}
                    >
                      {signMessage === true ? (
                        <Create
                          style={{
                            color:
                              theme.mode === "light"
                                ? theme.palette.primary.main
                                : "#EEE",
                          }}
                        />
                      ) : (
                        <Create style={{ color: "grey" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                {/* NOVO ÍCONE DE TRIGGER FLOW - APENAS EM TICKETS OPEN */}
                {ticketStatus === "open" && (
                  <Tooltip title="Disparar Fluxo">
                    <IconButton
                      aria-label="trigger-flow"
                      component="span"
                      onClick={handleTriggerFlowClick}
                      disabled={disableOption()}
                    >
                      <AccountTree
                        style={{
                          color: theme.mode === "light"
                            ? theme.palette.secondary.main
                            : "#EEE"
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {!isTicketPending() && (
                  <Tooltip title={i18n.t("messageInput.tooltip.privateMessage")}>
                    <IconButton
                      aria-label="send-upload"
                      component="span"
                      onClick={handlePrivateMessage}
                    >
                      {privateMessage === true ? (
                        <Comment
                          style={{
                            color:
                              theme.mode === "light"
                                ? theme.palette.primary.main
                                : "#EEE",
                          }}
                        />
                      ) : (
                        <Comment style={{ color: "grey" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Hidden>
            )}

            {isTicketPending() && (
              <Hidden only={["md", "lg", "xl"]}>
                <IconButton
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={handleOpenMenuClick}
                >
                  <MoreVert></MoreVert>
                </IconButton>
                <Menu
                  id="simple-menu"
                  keepMounted
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuItemClick}
                >
                  <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    setShowEmoji((prevState) => !prevState);
                    handleMenuItemClick();
                  }}>
                    <IconButton
                      aria-label="emojiPicker"
                      component="span"
                      disabled={disableOptionForPending()}
                    >
                      <Mood className={classes.sendMessageIcons} />
                    </IconButton>
                    Emoji
                  </MenuItem>
                </Menu>
              </Hidden>
            )}

            {!isTicketPending() && (
              <Hidden only={["md", "lg", "xl"]}>
                <IconButton
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={handleOpenMenuClick}
                >
                  <MoreVert></MoreVert>
                </IconButton>
                <Menu
                  id="simple-menu"
                  keepMounted
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuItemClick}
                >
                  <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    setShowEmoji((prevState) => !prevState);
                    handleMenuItemClick();
                  }}>
                    <IconButton
                      aria-label="emojiPicker"
                      component="span"
                      disabled={disableOption()}
                    >
                      <Mood className={classes.sendMessageIcons} />
                    </IconButton>
                    Emoji
                  </MenuItem>
                  <MenuItem>
                    <input
                      multiple
                      type="file"
                      id="upload-img-button-mobile"
                      accept="image/*, video/*, audio/* "
                      disabled={disableOption()}
                      className={classes.uploadInput}
                      onChange={(e) => {
                        handleChangeMedias(e);
                        handleMenuItemClick();
                      }}
                    />
                    <label htmlFor="upload-img-button-mobile" style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
                      <Fab
                        aria-label="upload-img"
                        component="span"
                        className={classes.invertedFabMenuMP}
                        disabled={disableOption()}
                      >
                        <PermMedia />
                      </Fab>
                      <span style={{ marginLeft: 8 }}>{i18n.t("messageInput.type.imageVideo")}</span>
                    </label>
                  </MenuItem>
                  <MenuItem onClick={handleCameraModalOpen}>
                    <Fab className={classes.invertedFabMenuCamera} disabled={disableOption()}>
                      <CameraAlt />
                    </Fab>
                    {i18n.t("messageInput.type.cam")}
                  </MenuItem>
                  <MenuItem>
                    <input
                      multiple
                      type="file"
                      id="upload-doc-button-mobile"
                      accept="application/*, text/*, .odt, .ods, .odp, .odg, .xml, .ofx, .zip, .rar, .7z, .tar, .gz, .bz2, .msg, .key, .numbers, .pages"
                      disabled={disableOption()}
                      className={classes.uploadInput}
                      onChange={(e) => {
                        handleChangeMedias(e);
                        handleMenuItemClick();
                      }}
                    />
                    <label htmlFor="upload-doc-button-mobile" style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
                      <Fab
                        aria-label="upload-img"
                        component="span"
                        className={classes.invertedFabMenuDoc}
                        disabled={disableOption()}
                      >
                        <Description />
                      </Fab>
                      <span style={{ marginLeft: 8 }}>Documento</span>
                    </label>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleSendContactModalOpen();
                    handleMenuItemClick();
                  }}>
                    <Fab className={classes.invertedFabMenuCont} disabled={disableOption()}>
                      <Person />
                    </Fab>
                    {i18n.t("messageInput.type.contact")}
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleSendLinkVideo();
                    handleMenuItemClick();
                  }}>
                    <Fab className={classes.invertedFabMenuMeet} disabled={disableOption()}>
                      <Duo />
                    </Fab>
                    {i18n.t("messageInput.type.meet")}
                  </MenuItem>
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setLocationModalOpen(true); handleMenuItemClick(); }}>
                      <Fab className={classes.invertedFabMenuCont} style={{ color: "#4CAF50" }}>
                        <LocationOnIcon />
                      </Fab>
                      Localização
                    </MenuItem>
                  )}
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setPollModalOpen(true); handleMenuItemClick(); }}>
                      <Fab className={classes.invertedFabMenuCont} style={{ color: "#7C4DFF" }}>
                        <PollIcon />
                      </Fab>
                      Enquete
                    </MenuItem>
                  )}
                  {["whatsapp", "wuzapi"].includes(ticketChannel) && (
                    <MenuItem onClick={() => { setStickerPickerOpen(true); handleMenuItemClick(); }}>
                      <Fab
                        className={classes.invertedFabMenuCont}
                        style={{ color: "#FF9800" }}
                      >
                        <span role="img" aria-label="sticker" style={{ fontSize: 22 }}>🏷️</span>
                      </Fab>
                      Figurinhas
                    </MenuItem>
                  )}
                  {useWhatsappOfficial &&
                    ticketChannel === "whatsapp_oficial" && (
                      <MenuItem onClick={() => {
                        handleSendTemplate();
                        handleMenuItemClick();
                      }}>
                        <Fab
                          className={classes.invertedFabMenuMeet}
                          style={needsTemplate ? {
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff'
                          } : {}}
                        >
                          <WhatsApp />
                        </Fab>
                        {i18n.t("messageInput.type.template")}
                        {needsTemplate && <span style={{ marginLeft: 8, color: '#FF9800' }}>⚠️</span>}
                      </MenuItem>
                    )}
                  {signMessagePar && (
                    <MenuItem onClick={() => {
                      handleChangeSign();
                      handleMenuItemClick();
                    }}>
                      <Tooltip title="Habilitar/Desabilitar Assinatura">
                        <IconButton
                          aria-label="send-upload"
                          component="span"
                        >
                          {signMessage === true ? (
                            <Create
                              style={{
                                color:
                                  theme.mode === "light"
                                    ? theme.palette.primary.main
                                    : "#EEE",
                              }}
                            />
                          ) : (
                            <Create style={{ color: "grey" }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      Assinatura
                    </MenuItem>
                  )}

                  {/* NOVO ITEM DE MENU MOBILE PARA TRIGGER FLOW */}
                  {ticketStatus === "open" && (
                    <MenuItem onClick={() => {
                      handleMenuItemClick();
                      handleTriggerFlowClick();
                    }}>
                      <IconButton
                        aria-label="trigger-flow"
                        component="span"
                      >
                        <AccountTree
                          style={{
                            color: theme.mode === "light"
                              ? theme.palette.secondary.main
                              : "#EEE"
                          }}
                        />
                      </IconButton>
                      Disparar Fluxo
                    </MenuItem>
                  )}

                  <MenuItem onClick={() => {
                    handlePrivateMessage();
                    handleMenuItemClick();
                  }}>
                    <Tooltip title="Habilitar/Desabilitar Comentários">
                      <IconButton
                        aria-label="send-upload"
                        component="span"
                      >
                        {privateMessage === true ? (
                          <Comment
                            style={{
                              color:
                                theme.mode === "light"
                                  ? theme.palette.primary.main
                                  : "#EEE",
                            }}
                          />
                        ) : (
                          <Comment style={{ color: "grey" }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    Comentários
                  </MenuItem>
                </Menu>
              </Hidden>
            )}

            {/* Emoji Picker para Mobile */}
            <Hidden only={["md", "lg", "xl"]}>
              {showEmoji && (
                <div className={classes.emojiBox} style={{ 
                  bottom: 63, 
                  left: 0, 
                  right: 0, 
                  width: '100%',
                  zIndex: 9999 
                }}>
                  <ClickAwayListener 
                    onClickAway={(e) => {
                      // Não fechar se o clique foi no input ou em elementos relacionados
                      if (inputRef.current && (
                        inputRef.current.contains(e.target) || 
                        e.target === inputRef.current ||
                        inputRef.current.parentElement?.contains(e.target)
                      )) {
                        return;
                      }
                      // Fechar apenas se clicar realmente fora
                      setShowEmoji(false);
                    }}
                  >
                    <Picker
                      perLine={7}
                      theme={"dark"}
                      i18n={i18n}
                      showPreview={false}
                      showSkinTones={false}
                      onSelect={handleAddEmoji}
                      style={{ width: '100%' }}
                    />
                  </ClickAwayListener>
                </div>
              )}
            </Hidden>

            <div className={classes.flexContainer}>
              {(privateMessageInputVisible || isTicketPending()) && (
                <div className={classes.flexItem}>
                  <div className={isTicketPending() ? classes.messageInputWrapperPending : classes.messageInputWrapperPrivate}>
                    <InputBase
                      inputRef={(input) => {
                        inputRef.current = input;
                      }}
                      className={isTicketPending() ? classes.messageInputPending : classes.messageInputPrivate}
                      placeholder={
                        isTicketPending()
                          ? "Mensagem interna (ticket aguardando aceite)..."
                          : ticketStatus === "open" || ticketStatus === "group"
                            ? i18n.t("messagesInput.placeholderPrivateMessage")
                            : i18n.t("messagesInput.placeholderClosed")
                      }
                      multiline
                      rows={1}
                      maxRows={5}
                      value={safeCapitalizeFirstLetter(inputMessage)}
                      onChange={handleChangeInput}
                      disabled={disableOptionForPending()}
                      onFocus={() => {
                        // Fechar modal de emojis apenas se o foco NÃO veio de uma seleção de emoji
                        // Isso permite que o usuário selecione vários emojis sem fechar o modal
                        if (showEmoji && !emojiFocusRef.current) {
                          setShowEmoji(false);
                        }
                      }}
                      onPaste={(e) => {
                        (ticketStatus === "open" || ticketStatus === "group" || isTicketPending()) &&
                          handleInputPaste(e);
                      }}
                      onKeyDown={handleKeyDown}
                      onKeyUp={handleKeyUp}
                      onMouseUp={handleMouseUp}
                      onSelect={handleSelectText}
                      spellCheck={true}
                    />
                    {showMentionList && !typeBar && mentionListFiltered.length > 0 && (
                      <Box
                        component="ul"
                        className={classes.messageQuickAnswersWrapper}
                        style={{ marginBottom: "8px" }}
                      >
                        <li style={{ listStyle: "none", padding: "4px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                            Mencionar participante
                          </Typography>
                        </li>
                        {mentionListFiltered.slice(0, 10).map((participant, index) => {
                          const isSelected = selectedMentionIndex === index;
                          return (
                            <li key={participant.jid} style={{ listStyle: "none" }}>
                              <div
                                className={clsx(
                                  classes.quickAnswerItem,
                                  isSelected && classes.quickAnswerItemSelected
                                )}
                                style={{
                                  ...(isSelected && {
                                    backgroundColor: "#e3f2fd",
                                    borderLeft: "4px solid #1976d2",
                                  })
                                }}
                                onClick={() => handleMentionSelect(participant)}
                              >
                                <Box style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                                  <Person style={{ fontSize: 20, color: participant.jid === "all" ? "#4caf50" : "#757575" }} />
                                  <Box>
                                    <Typography variant="body2" style={{ fontWeight: participant.jid === "all" ? 600 : 400 }}>
                                      {participant.name}
                                    </Typography>
                                    {participant.number && (
                                      <Typography variant="caption" color="textSecondary">
                                        {participant.number}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </div>
                            </li>
                          );
                        })}
                        {mentionListFiltered.length > 10 && (
                          <li style={{ listStyle: "none", textAlign: "center", padding: "4px", fontSize: "0.75rem", color: "#999" }}>
                            +{mentionListFiltered.length - 10} participantes • continue digitando...
                          </li>
                        )}
                      </Box>
                    )}
                    {typeBar ? (
                      <Box
                        component="ul"
                        className={classes.messageQuickAnswersWrapper}
                        style={{
                          marginBottom: "8px" // ✅ Pequeno espaço entre input e lista
                        }}
                      >
                        {typeBar.map((value, index) => {
                          const isSelected = selectedQuickAnswerIndex === index;
                          return (
                            <li
                              className={classes.messageQuickAnswersWrapperItem}
                              key={index}
                              style={{ listStyle: "none" }}
                            >
                              <div
                                className={clsx(
                                  classes.quickAnswerItem,
                                  isSelected && classes.quickAnswerItemSelected
                                )}
                                style={{
                                  ...getQuickAnswerItemStyle(index),
                                  // ✅ Estilo adicional para item selecionado
                                  ...(isSelected && {
                                    backgroundColor: theme.palette.primary.light + "20",
                                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                                    transform: "translateX(2px)" // ✅ Leve deslocamento visual
                                  })
                                }}
                                onClick={() => handleQuickAnswersClick(value)}
                              >
                                <Box className={classes.quickAnswerText}>
                                  {value.label}
                                </Box>
                                {value.mediaType && (
                                  <Chip
                                    size="small"
                                    label={`${getMediaTypeIcon(value.mediaType)} ${value.mediaType}`}
                                    color={getMediaTypeColor(value.mediaType)}
                                    className={classes.mediaTypeChip}
                                  />
                                )}
                              </div>
                            </li>
                          );
                        })}

                        {/* ✅ Indicador de scroll apenas se necessário */}
                        {typeBar.length > 4 && (
                          <li style={{ listStyle: "none" }}>
                            <div className={classes.quickAnswersScrollIndicator}>
                              📋 {typeBar.length} respostas • use ↑↓ para navegar
                            </div>
                          </li>
                        )}
                      </Box>
                    ) : null}
                  </div>
                </div>
              )}
              {!privateMessageInputVisible && !isTicketPending() && (
                <div className={classes.flexItem}>
                  <div className={classes.messageInputWrapper}>
                    <InputBase
                      inputRef={(input) => {
                        inputRef.current = input;
                      }}
                      className={classes.messageInput}
                      placeholder={placeholderText}
                      multiline
                      rows={1}
                      maxRows={5}
                      value={safeCapitalizeFirstLetter(inputMessage)}
                      onChange={handleChangeInput}
                      disabled={disableOption()}
                      onFocus={() => {
                        // Fechar modal de emojis apenas se o foco NÃO veio de uma seleção de emoji
                        // Isso permite que o usuário selecione vários emojis sem fechar o modal
                        if (showEmoji && !emojiFocusRef.current) {
                          setShowEmoji(false);
                        }
                      }}
                      onPaste={(e) => {
                        (ticketStatus === "open" || ticketStatus === "group") &&
                          handleInputPaste(e);
                      }}
                      onKeyDown={handleKeyDown}
                      onKeyUp={handleKeyUp}
                      onMouseUp={handleMouseUp}
                      onSelect={handleSelectText}
                      spellCheck={true}
                    />
                    {showMentionList && !typeBar && mentionListFiltered.length > 0 && (
                      <Box
                        component="ul"
                        className={classes.messageQuickAnswersWrapper}
                        style={{ marginBottom: "8px" }}
                      >
                        <li style={{ listStyle: "none", padding: "4px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                            Mencionar participante
                          </Typography>
                        </li>
                        {mentionListFiltered.slice(0, 10).map((participant, index) => {
                          const isSelected = selectedMentionIndex === index;
                          return (
                            <li key={participant.jid} style={{ listStyle: "none" }}>
                              <div
                                className={clsx(
                                  classes.quickAnswerItem,
                                  isSelected && classes.quickAnswerItemSelected
                                )}
                                style={{
                                  ...(isSelected && {
                                    backgroundColor: "#e3f2fd",
                                    borderLeft: "4px solid #1976d2",
                                  })
                                }}
                                onClick={() => handleMentionSelect(participant)}
                              >
                                <Box style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                                  <Person style={{ fontSize: 20, color: participant.jid === "all" ? "#4caf50" : "#757575" }} />
                                  <Box>
                                    <Typography variant="body2" style={{ fontWeight: participant.jid === "all" ? 600 : 400 }}>
                                      {participant.name}
                                    </Typography>
                                    {participant.number && (
                                      <Typography variant="caption" color="textSecondary">
                                        {participant.number}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </div>
                            </li>
                          );
                        })}
                        {mentionListFiltered.length > 10 && (
                          <li style={{ listStyle: "none", textAlign: "center", padding: "4px", fontSize: "0.75rem", color: "#999" }}>
                            +{mentionListFiltered.length - 10} participantes • continue digitando...
                          </li>
                        )}
                      </Box>
                    )}
                    {typeBar ? (
                      <ul className={classes.messageQuickAnswersWrapper}>
                        {typeBar.map((value, index) => {
                          const isSelected = selectedQuickAnswerIndex === index;
                          return (
                            <li
                              className={classes.messageQuickAnswersWrapperItem}
                              key={index}
                            >
                              <div
                                className={`${classes.quickAnswerItem} ${isSelected ? 'selected' : ''}`}
                                style={getQuickAnswerItemStyle(index)}
                                onClick={() => handleQuickAnswersClick(value)}
                              >
                                <Box className={classes.quickAnswerText}>
                                  {value.label}
                                </Box>
                                {value.mediaType && (
                                  <Chip
                                    size="small"
                                    label={`${getMediaTypeIcon(value.mediaType)} ${value.mediaType}`}
                                    color={getMediaTypeColor(value.mediaType)}
                                    className={classes.mediaTypeChip}
                                  />
                                )}
                              </div>
                            </li>
                          );
                        })}

                        {typeBar.length > 5 && (
                          <div className={classes.quickAnswersScrollIndicator}>
                            {typeBar.length} respostas • role para ver mais
                          </div>
                        )}
                      </ul>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {(!privateMessageInputVisible || isTicketPending()) && (
              <>
                {showSchedules && !isTicketPending() && (
                  <Tooltip
                    title={
                      hasScheduleDraft
                        ? i18n.t("scheduleModal.draft.badgeTooltip")
                        : i18n.t("tickets.buttons.scredule")
                    }
                  >
                    <IconButton
                      aria-label="scheduleMessage"
                      component="span"
                      onClick={() => setAppointmentModalOpen(true)}
                      disabled={loading}
                    >
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={!hasScheduleDraft}
                        overlap="circular"
                      >
                        <Timer className={classes.sendMessageIcons} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
                {inputMessage || showSelectMessageCheckbox ? (
                  <>
                    <IconButton
                      aria-label="sendMessage"
                      component="span"
                      onClick={
                        showSelectMessageCheckbox
                          ? handleOpenModalForward
                          : handleSendMessage
                      }
                      disabled={loading}
                    >
                      {showSelectMessageCheckbox ? (
                        <Reply className={classes.ForwardMessageIcons} />
                      ) : (
                        <Send className={classes.sendMessageIcons} />
                      )}
                    </IconButton>
                  </>
                ) : recording ? (
                  <div className={classes.recorderWrapper}>
                    <IconButton
                      aria-label="cancelRecording"
                      component="span"
                      fontSize="large"
                      disabled={loading}
                      onClick={handleCancelAudio}
                    >
                      <HighlightOff className={classes.cancelAudioIcon} />
                    </IconButton>
                    {loading ? (
                      <div>
                        <CircularProgress className={classes.audioLoading} />
                      </div>
                    ) : (
                      <RecordingTimer />
                    )}

                    <IconButton
                      aria-label="sendRecordedAudio"
                      component="span"
                      onClick={handleUploadAudio}
                      disabled={loading}
                    >
                      <CheckCircleOutline className={classes.sendAudioIcon} />
                    </IconButton>
                  </div>
                ) : (
                  <IconButton
                    aria-label="showRecorder"
                    component="span"
                    disabled={disableOptionForPending()}
                    onClick={handleStartRecording}
                  >
                    <Mic className={classes.sendMessageIcons} />
                  </IconButton>
                )}
              </>
            )}

            {privateMessageInputVisible && !isTicketPending() && (
              <>
                <IconButton
                  aria-label="sendMessage"
                  component="span"
                  onClick={
                    showSelectMessageCheckbox
                      ? handleOpenModalForward
                      : handleSendMessage
                  }
                  disabled={loading}
                >
                  {showSelectMessageCheckbox ? (
                    <Reply className={classes.ForwardMessageIcons} />
                  ) : (
                    <Send className={classes.sendMessageIcons} />
                  )}
                </IconButton>
              </>
            )}

            {pixModalOpen && (
              <PixSendModal
                open={pixModalOpen}
                onClose={() => setPixModalOpen(false)}
                ticketId={ticketId}
              />
            )}

            {locationModalOpen && (
              <LocationSendModal
                open={locationModalOpen}
                onClose={() => setLocationModalOpen(false)}
                ticketId={ticketId}
              />
            )}

            {pollModalOpen && (
              <PollSendModal
                open={pollModalOpen}
                onClose={() => setPollModalOpen(false)}
                ticketId={ticketId}
              />
            )}

            {stickerPickerOpen && (
              <StickerPicker
                open={stickerPickerOpen}
                onClose={() => setStickerPickerOpen(false)}
                ticketId={ticketId}
              />
            )}

            {appointmentModalOpen && (
              <ScheduleModal
                open={appointmentModalOpen}
                onClose={() => setAppointmentModalOpen(false)}
                message={inputMessage}
                contactId={contactId}
                ticketId={ticketId}
                onDraftStatusChange={setHasScheduleDraft}
                fromMessageInput={true}
                user={user}
                ticketWhatsappId={whatsappId}
                ticketUserId={ticketUserId}
                ticketQueueId={ticketQueueId}
              />
            )}

            {/* Menu de formatação que aparece quando texto é selecionado */}
            <TextFormatMenu />

          </div>
        </Paper>
      </>
    );
  }
};

export default MessageInput;